import { useEffect, useRef, useState, lazy } from 'react';
import styles from './BrowseEvents.module.css';
import { useLocation } from 'react-router-dom';
import FilterSection from '../components/FilterSection';
import { useSharedState } from '../hooks/useSharedState';
import { DateUtil } from '../utils/DateUtil';
import { timeOptions } from '../components/TimePicker';
import SearchBar from '../components/SearchBar';
import { DEFAULT_CITY } from '../utils/GlobalUtil';
import SubscriptionSection from '../components/SubscriptionSection';
import sortiesFamille from '../assets/sortiesFamille.jpg';
import activitesGratuites from '../assets/activitesGratuites.jpg';
import spectacleMusique from '../assets/spectacleMusique.jpg';
const EventList = lazy(() => import('../components/EventList')); // big list
import WeeklyHighlight from '../components/WeeklyHighlight';
import arrow from '../assets/arrowRight.png';
import { useEvents } from '../hooks/useEvents';

function BrowseEvents() {

  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [events, updateEvents] = useEvents();
  const [categories, setCategories] = useSharedState('categories', '');
  const [free, setFree] = useSharedState('free', '');
  const [period, setPeriod] = useSharedState('period', '');
  const [time, setTime] = useSharedState('time', '');
  const [searchQuery, setSearchQuery] = useSharedState('searchQuery', '');
  const [city, setCity] = useSharedState('city', DEFAULT_CITY);
  const [venue, setVenue] = useSharedState('venue', '');
  const [latitude, setLatitude] = useSharedState('latitude', '');
  const [longitude, setLongitude] = useSharedState('longitude', '');

  const [initialized, setInitialized] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!getScroll()) {
      window.scrollTo(0, 0); // Scroll to the top of the page
    }
  }, []);

  useEffect(() => {
    
    if (!initialized) return;
    setUrlParamsFromState();
    fetchEvents();
  }, [initialized, categories, free, period, time, searchQuery, city, venue, latitude, longitude]);

  useEffect(() => {

    const params = new URLSearchParams(location.search);
    
    const categoryParam = params.get('cat');
    if (categoryParam != null) {
      setCategories(categoryParam ?? categories);
    }

    const freeParam = params.get('f');
    setFree(freeParam == '1' ? '1' : '0');

    const periodParam = params.get('p');
    // si le param est null, on prend aujourd'hui par défaut
    setPeriod(periodParam ?? DateUtil.formatDateToLocalYMD(new Date())); 

    const timeParam = params.get('t');
    if (timeParam || !time) {
      setTime(timeParam ?? timeOptions.TOUTE_HEURE);
    }

    const qParam = params.get('q');
    if (qParam != null) {
      setSearchQuery(qParam);
    }

    const cityParam = params.get('c');
    setCity(cityParam ?? DEFAULT_CITY);

    const venueParam = params.get('v');
    if (venueParam) {
      setVenue(venueParam ?? '');
    }

    const latParam = params.get('lat');
    if (latParam) {
      setLatitude(latParam ?? '');
    }

    const longParam = params.get('long');
    if (longParam) {
      setLongitude(longParam ?? '');
    }

    setInitialized(true);
    
  }, [location.search]); // Re-run if the location changes

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  const setUrlParamsFromState = () => {
    const params = new URLSearchParams(location.search);
    
    params.set('cat', categories);
    params.set('f', free);
    params.set('p', period);
    params.set('t', time);
    params.set('q', searchQuery);
    params.set('c', city);
    params.set('v', venue);
    params.set('lat', latitude);
    params.set('long', longitude);
    window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
  };

  const getScroll = (): number => {
    const scrollPos = sessionStorage.getItem('scrollPosition');
    return scrollPos ? parseInt(scrollPos) || 0 : 0;
  };

  const deleteScroll = () => {
    sessionStorage.removeItem('scrollPosition');
  };

  const fetchEvents = async () => {
    setIsLoading(true);
    return new Promise<void>(async (resolve, reject) => {
      try {

        let startDate = "";
        let endDate = "";

        if (period == "thisWeek") {
          const weekDays = DateUtil.getWeekDays();
          startDate = weekDays.monday;
          endDate = weekDays.sunday;
        }
        else if (period == "nextWeek") {
          const weekDays = DateUtil.getWeekDays(true);
          startDate = weekDays.monday;
          endDate = weekDays.sunday;
        }
        else if (parseInt(period) >= 0 && parseInt(period) < 12) {
          const monthDays = getFirstAndLastDayOfMonth(parseInt(period));
          startDate = monthDays.firstDay;
          endDate = monthDays.lastDay;
        }
        else if (DateUtil.isValidDateString(period)) {
          startDate = period;
        }
        if (startDate) { //make sure we have a startDate otherwise it will load all events
          let fetchUrl = '/api/events?category=' + categories + '&timezone=EST&date=' + startDate;
          if (searchQuery) {
            fetchUrl += '&searchQuery=' + searchQuery;
          }
          if (city) {
            fetchUrl += '&city=' + city;
          }
          if (endDate) {
            fetchUrl += '&endDate=' + endDate;
          }
          if (time) {
            fetchUrl += '&time=' + time;
          }
          if (venue) {
            fetchUrl += '&venue=' + venue;
          }
          if (free == '1') {
            fetchUrl += '&free=1';
          }
          if (latitude && longitude) {
            fetchUrl += '&lat=46.81389902358164&long=-71.21664211898582';
          }

          const response = await fetch(fetchUrl);
          updateEvents(await response.json());
          const savedScroll = getScroll();
          if (savedScroll) {
            window.scrollTo(0, getScroll());
            deleteScroll();
          }
          setIsLoading(false);
          resolve();

        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setIsLoading(false);
        reject();
      }
    });
  }

  const getFirstAndLastDayOfMonth = (monthIndex: number) => {

    const currentMonthIndex = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const year = monthIndex >= currentMonthIndex ? currentYear : currentYear + 1;

    // First day of the month
    const firstDate = new Date(year, monthIndex, 1);
    const firstDay = firstDate.toISOString().split('T')[0];

    // Last day of the month
    const lastDate = new Date(year, monthIndex + 1, 0); // Day 0 of next month is the last day of current month
    const lastDay = lastDate.toISOString().split('T')[0];

    return { firstDay, lastDay };
  };

  const quickSelect = (category: string) => {
    setCategories(category);
    const el = document.querySelector("#search");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const quickSelectFree = () => {
    setFree('1');
    const el = document.querySelector("#search");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>

      <div className={styles.quickSelect}>
        <h1>Découvrez tout ce qui se passe autour de vous</h1>
        <h2>Des idées de sorties pour tous les goûts, simples à explorer.</h2>
        <div className={styles.categories}>
          <div className={styles.category} onClick={() => quickSelect('famille')}>
            <img src={sortiesFamille} alt="Sorties en famille" />
            <h1>Sorties <br /> en famille</h1>
          </div>
          <div className={styles.category} onClick={() => quickSelectFree()}>
            <img src={activitesGratuites} alt="Activités gratuites" />
            <h1>Activités <br /> gratuites</h1>
          </div>
          <div className={styles.category} onClick={() => quickSelect('musique')}>
            <img src={spectacleMusique} alt="Musique" />
            <h1>Musique</h1>
          </div>
        </div>
      </div>

      <SubscriptionSection />

      <h1 className={styles.search} id="search">Tous les événements à Québec par catégorie</h1>
      <div ref={scrollRef}></div>
      <SearchBar />
      <FilterSection />

      <EventList events={events} />
      {!isLoading && events.length == 0 && <div className={styles.noResults}>
        Aucun événement ne correspond à vos critères :(
      </div>}

      <WeeklyHighlight />

      <div className={styles.callToAction}>
        <h2>Tu ne trouves pas l'événement que tu cherches?</h2>
        <h2>Tu aimerais ajouter un événement à cette page?</h2>
        <h2>Tu as trouvé une erreur?</h2>

        <a href="mailto:contact@alentours.ca"><button type="button" className={styles.callToActionBtn}>Écris-nous!</button></a>

        <img className={styles.arrow} src={arrow} alt="flèche" />

      </div>
    </>
  );
}

export default BrowseEvents
